import React, { useState } from "react";
import {
  Flex,
  Accordion as ChakraAccordion,
  type SpaceProps,
  AspectRatio,
  Box,
  type AspectRatioProps,
} from "@chakra-ui/react";
import { remCalc } from "theme/utils";
import { desktopOnlyDisplay } from "utils";
import { type Theme } from "contexts/color-theme";
import { AnimatePresence } from "framer-motion";
import { MotionBox } from "ui/chakra-motion";
import { handleClientError } from "lib/errors";
import { AccordionItem, type AccordionItemContent } from "./accordion-item";

type BaseAccordionProps = {
  /**
   * The color theme of the accordion.
   *
   * @default light
   */
  theme?: Theme;

  /**
   * The size of the media.
   *
   * @default "sm"
   */
  size?: "sm" | "lg";

  /**
   * The direction of the media.
   *
   * @default "left"
   */
  direction?: "left" | "right";

  /**
   * A specific aspect ratio to use for the media section.
   *
   * @default 1 / 1 for "sm" media and 872 / 648 for "lg" media
   */
  mediaRatio?: AspectRatioProps["ratio"];

  /**
   * Option to include a border around the media.
   */
  hasBorder?: boolean;

  /**
   * Content for the individual accordion items.
   */
  items: AccordionItemContent[];
} & SpaceProps;

type DynamicVariantProps = BaseAccordionProps & {
  variant: "dynamic";

  /**
   * This setting is used to prerender all accordion media items in the DOM.
   * This allows all static image files to load at once, fixing any "flashing" effect
   * when a new accordion item is clicked.
   *
   * Turned on by default, and only disabled by explicitly passing in `false`.
   */
  prerenderMedia?: boolean;
};

type StaticVariantProps = BaseAccordionProps & {
  variant: "static";

  /**
   * Static media to display in the accordion. Does not toggle with the accordion.
   * Can be either an image or lottie animation, but must be a single JSX Element.
   */
  media: JSX.Element;
};

export type MediaAccordionProps = StaticVariantProps | DynamicVariantProps;

export function MediaAccordion({
  size = "sm",
  direction = "left",
  theme = "light",
  items,
  mediaRatio,
  hasBorder,
  ...props
}: MediaAccordionProps) {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const ratio = mediaRatio ?? (size === "sm" ? 1 / 1 : 872 / 648);

  // Destructure extra props from the object passed to the Chakra accordion to fix unknown prop console errors and clean up the DOM
  // Done by reassigning a separate object to most easily support the discriminated union prop typing
  let accordionProps;

  let media: JSX.Element;
  if (props.variant === "static") {
    const { variant, media: staticMedia, ...rest } = props;
    accordionProps = rest;

    media = staticMedia;
  } else {
    const { variant, prerenderMedia, ...rest } = props;
    accordionProps = rest;

    const activeMedia = items[expandedIndex]?.media;
    if (!activeMedia) {
      handleClientError("A dynamic media accordion is missing media for one of its active items.");
    }

    media = (
      <AnimatePresence initial={false}>
        {activeMedia && (
          <MotionBox
            key={items[expandedIndex].title}
            boxSize="100%"
            pos="relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.25 } }}
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
          >
            {activeMedia}
          </MotionBox>
        )}
      </AnimatePresence>
    );
  }

  let prerenderedMedia = null;
  if (props.variant === "dynamic" && props.prerenderMedia !== false) {
    // Dynamic media will be prerendered by default, and is only disabled by explicitly passing in a false value
    // This is rendered independent of the primary media element to enable mobile support, when media is rendered within the accordion items
    prerenderedMedia = (
      <Box pos="absolute" boxSize="0px" opacity={0} aria-hidden="true">
        {items.map((item) => (
          <Box key={item.title}>{item.media}</Box>
        ))}
      </Box>
    );
  }

  return (
    <ChakraAccordion
      // Only static variants can support toggling.
      // Dynamic variants require a media element for each active index, and can never be null
      allowToggle={props.variant === "static"}
      defaultIndex={0}
      display="flex"
      alignItems="center"
      flexDir={{ base: "column", md: direction === "right" ? "row-reverse" : "row" }}
      gap={{ base: "smallest", md: "biggest" }}
      justifyContent={{ base: "center", md: "space-between" }}
      onChange={(index) => {
        // Since we do not support `allowMultiple`, we can safely cast the index to a number
        setExpandedIndex(index as number);
      }}
      {...accordionProps}
    >
      <AspectRatio
        border={hasBorder ? "1px" : "none"}
        borderColor={theme === "light" ? "brand.border.extraLight" : "brand.border.dark"}
        borderRadius={{ base: "small", md: "bigger" }}
        ratio={ratio}
        flex={1}
        w={{ base: "100%", md: size === "sm" ? "50%" : "66%" }}
        maxW={{ base: "mediaMaxW.mobile", md: "100%", lg: size === "sm" ? "648px" : "872px" }}
        pos="relative"
        overflow="hidden"
        display={props.variant === "dynamic" ? desktopOnlyDisplay() : "block"}
      >
        {media}
      </AspectRatio>

      <Flex
        direction="column"
        flex={1}
        w={{ base: "100%", md: size === "sm" ? remCalc(425) : remCalc(324) }}
        maxW={{ base: "100%", md: size === "sm" ? remCalc(425) : remCalc(324) }}
        mx="auto"
      >
        {items.map((item) => {
          return <AccordionItem key={item.id ?? item.title} theme={theme} mediaRatio={ratio} {...item} />;
        })}
      </Flex>

      {prerenderedMedia}
    </ChakraAccordion>
  );
}
